/**
 * useAccountDeletionPort — port-factory hook returning a stable
 * {@link AccountDeletionPort} adapter for production composition (033 US2,
 * T029).
 *
 * The composer (`Composition`) calls this once at the root and passes the
 * instance through `PortsProvider` as the `accountDeletion` slot. Like
 * `useMyDataPort`, it constructs the REAL tRPC-backed adapter
 * (`TrpcAccountDeletionAdapter` over `member.myData.requestDeletion`) — the
 * member's cookie session authenticates the same-origin request, so no bearer
 * source is wired (the `useContentPort` / `useMyDataPort` posture).
 *
 * Test harnesses (`renderAppWithPorts`) deliberately do NOT call this factory —
 * the `accountDeletion` slot stays omitted by default so the missing-port stub
 * semantics hold; tests that exercise the deletion surface pass a fixture port
 * explicitly.
 *
 * `useState(() => new TrpcAccountDeletionAdapter(...))[0]`: the lazy initialiser
 * runs once per mount; the setter is discarded because the adapter holds no
 * React state (the established `useXyzPort` mechanism).
 *
 * Owned by feature 033-data-ownership-and-lifecycle-foundations.
 */

import { useState } from 'react';

import type { AccountDeletionPort } from '../my-data/AccountDeletionPort.js';
import { TrpcAccountDeletionAdapter } from '../my-data/TrpcAccountDeletionAdapter.js';
import { createWebTrpcClient } from '../trpc/client.js';

export function useAccountDeletionPort(): AccountDeletionPort {
  const [port] = useState<AccountDeletionPort>(
    () => new TrpcAccountDeletionAdapter(createWebTrpcClient()),
  );
  return port;
}
